import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, ler, queryClient } from "../api.ts";
import { useEu } from "../router.tsx";
import { Area, Aviso, Botao, Cabecalho, Campo, Cartao, dataHora, Selo, Tabela } from "../ui.tsx";

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
type Relatorio = { fichas?: number; fontes?: number; refs?: number; pendentes?: unknown[] };

export function PaginaPublicar() {
  const eu = useEu();
  const [notas, setNotas] = useState("");
  const previa = useQuery({ queryKey: ["previa"], queryFn: () => ler(api.releases.previa.$get()) });
  const historico = useQuery({ queryKey: ["releases"], queryFn: () => ler(api.releases.$get()) });
  const publicar = useMutation({
    mutationFn: () => ler(api.releases.$post({ json: { notas: notas.trim() || undefined } })),
    onSuccess: () => {
      setNotas("");
      queryClient.invalidateQueries({ queryKey: ["previa"] });
      queryClient.invalidateQueries({ queryKey: ["releases"] });
    },
  });
  const p = previa.data;
  const revisor = eu?.papel === "admin" || eu?.papel === "revisor";
  const rel = p?.relatorio as Relatorio | undefined;

  return (
    <>
      <Cabecalho titulo="Publicação" sub="A release reúne as versões aprovadas e só sai se a cadeia ref → fato → fonte estiver completa." />
      {publicar.data ? (
        <Aviso tom="ok">
          Release <span className="font-mono">{publicar.data.versao}</span> publicada. O app baixa só as coleções alteradas:{" "}
          {publicar.data.alteradas.map((a) => a.nome).join(", ")}.
        </Aviso>
      ) : null}
      {publicar.error ? <Aviso>{msg(publicar.error)}</Aviso> : null}

      {p ? (
        <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
          <div className="flex min-w-0 flex-col gap-4">
            <Cartao titulo="Prévia da próxima release">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ["Fichas", rel?.fichas],
                  ["Fontes usadas", rel?.fontes],
                  ["Refs", rel?.refs],
                  ["Pendentes", rel?.pendentes?.length],
                ].map(([r, v]) => (
                  <div key={r} className="rounded-lg bg-surface-2 p-3">
                    <div className="font-mono text-xl font-semibold text-ink">{v ?? "—"}</div>
                    <div className="text-xs text-muted">{r}</div>
                  </div>
                ))}
              </div>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-muted">Em vigor</dt>
                <dd className="font-mono">{p.atual ? `${p.atual.versao} · ${dataHora(p.atual.publicadoEm)}` : "nenhuma"}</dd>
                <dt className="text-muted">Seria publicada</dt>
                <dd className="font-mono">{p.versao}</dd>
              </dl>
              {p.erros.length ? (
                <Aviso>
                  <p className="font-semibold">Publicação barrada: {p.erros.length} problema(s)</p>
                  <ul className="mt-1 list-disc pl-4 text-xs">
                    {p.erros.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </Aviso>
              ) : (
                <Aviso tom="ok">Validador sem bloqueios.</Aviso>
              )}
            </Cartao>

            <Cartao titulo={`Coleções alteradas (${p.alteradas.length})`}>
              {p.alteradas.length ? (
                <Tabela>
                  <thead>
                    <tr>
                      <th>Coleção</th>
                      <th>Hash atual</th>
                      <th>Novo hash</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.alteradas.map((a) => (
                      <tr key={a.nome}>
                        <td className="font-semibold">{a.nome}</td>
                        <td className="font-mono text-xs text-muted">{a.antes ?? "nova"}</td>
                        <td className="font-mono text-xs">{a.depois ?? "removida"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Tabela>
              ) : (
                <p className="text-sm text-muted">Nada mudou desde a release em vigor.</p>
              )}
            </Cartao>
          </div>

          <Cartao titulo="Publicar">
            <Campo rotulo="Notas da versão" dica="Visíveis no histórico; ex.: carência do Dectomax revista (E.M03).">
              <Area className="font-sans text-sm" rows={4} value={notas} onChange={(e) => setNotas(e.target.value)} />
            </Campo>
            <Botao
              variante="primario"
              disabled={!revisor || !!p.erros.length || !p.alteradas.length || publicar.isPending}
              title={!revisor ? "Só revisores publicam" : undefined}
              onClick={() => publicar.mutate()}
            >
              {publicar.isPending ? "Publicando…" : "Publicar agora"}
            </Botao>
            <p className="text-xs text-muted">
              Os apps recebem a nova versão na próxima sincronização; quem está offline continua com a anterior.
            </p>
          </Cartao>
        </div>
      ) : (
        <p className="text-sm text-muted">Montando prévia…</p>
      )}

      <Cartao titulo="Histórico de releases">
        <Tabela>
          <thead>
            <tr>
              <th>Versão</th>
              <th>Publicada</th>
              <th>Conteúdo</th>
              <th>Notas</th>
            </tr>
          </thead>
          <tbody>
            {historico.data?.map((r, i) => {
              const x = r.relatorio as Relatorio;
              return (
                <tr key={r.id}>
                  <td className="font-mono text-xs">
                    {r.versao} {i === 0 ? <Selo tom="ok">em vigor</Selo> : null}
                  </td>
                  <td className="whitespace-nowrap text-xs">{dataHora(r.publicadoEm)}</td>
                  <td className="whitespace-nowrap text-xs text-muted">
                    {x.fichas} fichas · {x.fontes} fontes · {x.refs} refs · {x.pendentes?.length ?? 0} pend.
                  </td>
                  <td className="text-xs">{r.notas ?? "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </Tabela>
      </Cartao>
    </>
  );
}
